import { PROP_TYPE_HINT, type RegistryEntry } from "@/src/shared_modules/custom_elements/types/customElements";
import { DS_PAGE_COLUMN_SLOT_PATTERN } from "@/src/design_system/layouts_and_containers/page/constants/slots";
import { DIALOG_PROP_TYPES, FIELDSET_PROP_TYPES, LIGHT_BOX_PROP_TYPES } from "./customElementPropTypes";
import { DS_CUSTOM_ELEMENT_NAME, type DSCustomElementName } from "./customElementNames";
import { DS_FORM_INPUT_CUSTOM_ELEMENTS_REGISTRY } from "./formInputCustomElements";
export {
  DS_CUSTOM_ELEMENT_NAME,
  type DSCustomElementName,
  customElementTag,
  SCRIPT_PROP_SELECTOR,
  EASY_CUSTOM_ELEMENTS_PREFIX,
  SCRIPT_PROPS_KEY,
} from "./customElementNames";

export const DS_CUSTOM_ELEMENTS_REGISTRY: Record<DSCustomElementName, RegistryEntry> = {
  [DS_CUSTOM_ELEMENT_NAME.LightBox]: {
    loadComponent: () => import("@/src/design_system/data_display/light_box/components/DSLightBox.vue"),
    propTypes: LIGHT_BOX_PROP_TYPES,
    emits: ["update:open", "update:activeIndex"],
    exposedMethods: ["show", "close", "next", "previous"],
    slots: ["default", "actions"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Table]: {
    loadComponent: () => import("@/src/design_system/data_display/table/components/DSTable.vue"),
    propTypes: {
      stickyHeader: PROP_TYPE_HINT.Boolean,
      stickyFirstColumn: PROP_TYPE_HINT.Boolean,
      stickyLastColumn: PROP_TYPE_HINT.Boolean,
    },
    // Static DOM projection supports headers, not per-row scoped cell content.
    slots: [/^header-.+$/],
  },
  [DS_CUSTOM_ELEMENT_NAME.Icon]: {
    loadComponent: () => import("@/src/design_system/primitives/icon/components/DSIcon.vue"),
    propTypes: { spin: PROP_TYPE_HINT.Boolean },
  },
  [DS_CUSTOM_ELEMENT_NAME.Flag]: {
    loadComponent: () => import("@/src/design_system/primitives/flag/components/DSFlag.vue"),
  },
  [DS_CUSTOM_ELEMENT_NAME.ContentConnector]: {
    loadComponent: () => import("@/src/design_system/primitives/content_connector/components/DSContentConnector.vue"),
    propTypes: {},
  },
  [DS_CUSTOM_ELEMENT_NAME.KeyboardShortcut]: {
    loadComponent: () => import("@/src/design_system/primitives/keyboard_shortcut/components/DSKeyboardShortcut.vue"),
    propTypes: {},
  },
  [DS_CUSTOM_ELEMENT_NAME.ResizeHandle]: {
    loadComponent: () => import("@/src/design_system/primitives/resize_handle/components/DSResizeHandle.vue"),
    modelValueType: PROP_TYPE_HINT.Number,
    propTypes: {
      min: PROP_TYPE_HINT.Number,
      max: PROP_TYPE_HINT.Number,
      step: PROP_TYPE_HINT.Number,
      disabled: PROP_TYPE_HINT.Boolean,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.Badge]: {
    loadComponent: () => import("@/src/design_system/data_display/badge/components/DSBadge.vue"),
    propTypes: { disabled: PROP_TYPE_HINT.Boolean, positionBottom: PROP_TYPE_HINT.Boolean },
  },
  [DS_CUSTOM_ELEMENT_NAME.Tag]: {
    loadComponent: () => import("@/src/design_system/data_display/tag/components/DSTag.vue"),
    propTypes: { removable: PROP_TYPE_HINT.Boolean, disabled: PROP_TYPE_HINT.Boolean },
  },
  [DS_CUSTOM_ELEMENT_NAME.PaletteItem]: {
    loadComponent: () => import("@/src/design_system/data_display/palette_item/components/DSPaletteItem.vue"),
    propTypes: {},
  },
  [DS_CUSTOM_ELEMENT_NAME.Thumbnail]: {
    loadComponent: () => import("@/src/design_system/data_display/thumbnail/components/DSThumbnail.vue"),
    propTypes: {
      loading: PROP_TYPE_HINT.Boolean,
      loadingProgress: PROP_TYPE_HINT.Number,
      showDescription: PROP_TYPE_HINT.Boolean,
    },
    slots: ["actions"],
  },
  [DS_CUSTOM_ELEMENT_NAME.FileItem]: {
    loadComponent: () => import("@/src/design_system/data_display/file_item/components/DSFileItem.vue"),
    propTypes: {
      showProgressBar: PROP_TYPE_HINT.Boolean,
      progress: PROP_TYPE_HINT.Number,
      progressMax: PROP_TYPE_HINT.Number,
      thumbnail: PROP_TYPE_HINT.Boolean,
      showAuthor: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      isClickable: PROP_TYPE_HINT.Boolean,
    },
    slots: ["actions"],
  },
  [DS_CUSTOM_ELEMENT_NAME.User]: {
    loadComponent: () => import("@/src/design_system/data_display/user/components/DSUser.vue"),
    propTypes: {
      hasAction: PROP_TYPE_HINT.Boolean,
      showAvatar: PROP_TYPE_HINT.Boolean,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.Button]: {
    loadComponent: () => import("@/src/design_system/button_and_controls/button/components/DSButton.vue"),
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
      fullWidth: PROP_TYPE_HINT.Boolean,
      tabindex: PROP_TYPE_HINT.Number,
      turbo: PROP_TYPE_HINT.Boolean,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.CopyButton]: {
    loadComponent: () => import("@/src/design_system/button_and_controls/copy_button/components/DSCopyButton.vue"),
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
      fullWidth: PROP_TYPE_HINT.Boolean,
      tabindex: PROP_TYPE_HINT.Number,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.EmojiPicker]: {
    loadComponent: () => import("@/src/design_system/button_and_controls/emoji_picker/components/DSEmojiPicker.vue"),
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
      fastSelect: PROP_TYPE_HINT.Boolean,
    },
    emits: ["update:modelValue"],
    exposedMethods: ["focus"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Alert]: {
    loadComponent: () => import("@/src/design_system/feedback/alert/components/DSAlert.vue"),
    propTypes: {
      closeable: PROP_TYPE_HINT.Boolean,
      autoDismiss: PROP_TYPE_HINT.Boolean,
      dismissTimeoutMs: PROP_TYPE_HINT.Number,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.ProgressBar]: {
    loadComponent: () => import("@/src/design_system/feedback/progress_bar/components/DSProgressBar.vue"),
    propTypes: { revert: PROP_TYPE_HINT.Boolean, value: PROP_TYPE_HINT.Number, max: PROP_TYPE_HINT.Number },
  },
  [DS_CUSTOM_ELEMENT_NAME.Grid]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/grid/components/DSGrid.vue"),
  },
  [DS_CUSTOM_ELEMENT_NAME.GridItem]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/grid/components/DSGridItem.vue"),
  },
  [DS_CUSTOM_ELEMENT_NAME.GridLayout]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/grid/components/DSGridLayout.vue"),
    propTypes: { dense: PROP_TYPE_HINT.Boolean },
  },
  [DS_CUSTOM_ELEMENT_NAME.Page]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/page/components/DSPage.vue"),
  },
  [DS_CUSTOM_ELEMENT_NAME.PageHeader]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/page/components/DSPageHeader.vue"),
    slots: ["actions"],
  },
  [DS_CUSTOM_ELEMENT_NAME.PageContent]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/page/components/DSPageContent.vue"),
  },
  // No `modelValueType`: array values are delivered through a JSON script prop.
  [DS_CUSTOM_ELEMENT_NAME.PageColumns]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/page/components/DSPageColumns.vue"),
    propTypes: { minColumnWidth: PROP_TYPE_HINT.Number },
    slots: [DS_PAGE_COLUMN_SLOT_PATTERN],
  },
  [DS_CUSTOM_ELEMENT_NAME.DragHandle]: {
    loadComponent: () => import("@/src/design_system/button_and_controls/drag_handle/components/DSDragHandle.vue"),
    propTypes: { disabled: PROP_TYPE_HINT.Boolean },
  },
  ...DS_FORM_INPUT_CUSTOM_ELEMENTS_REGISTRY,
  // Components with complex model values (arrays/objects) receive `value` as a JSON
  // `<script data-prop="value">` prop rendered by the Ruby side, so they intentionally
  // declare no `modelValueType`. A plain `data-value` attribute would arrive as a raw string.
  [DS_CUSTOM_ELEMENT_NAME.OptionList]: {
    emits: ["focus", "blur"],
    exposedMethods: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/option_list/components/DSOptionList.vue"),
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
      multiple: PROP_TYPE_HINT.Boolean,
      loading: PROP_TYPE_HINT.Boolean,
      keyboardNavigationEnabled: PROP_TYPE_HINT.Boolean,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.Menu]: {
    loadComponent: () => import("@/src/design_system/navigations/menu/components/DSMenu.vue"),
    propTypes: {},
    slots: ["prefix", "suffix"],
    emits: ["item:select", "submenu:toggle"],
  },
  [DS_CUSTOM_ELEMENT_NAME.MenuItem]: {
    loadComponent: () => import("@/src/design_system/navigations/menu/components/DSMenuItem.vue"),
    propTypes: {},
    slots: ["prefix", "suffix"],
    emits: ["select", "submenu:toggle"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Tabs]: {
    loadComponent: () => import("@/src/design_system/navigations/tabs/components/DSTabs.vue"),
    modelValueType: PROP_TYPE_HINT.String,
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
    },
    slots: ["suffix"],
  },
  // No `modelValueType` on purpose — see the script prop note above OptionList.
  [DS_CUSTOM_ELEMENT_NAME.Select]: {
    emits: ["focus", "blur", "search", "update:modelValue"],
    loadComponent: () => import("@/src/design_system/form_elements/select/components/DSSelect.vue"),
    propTypes: {
      inlineEdit: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      multiple: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      autocomplete: PROP_TYPE_HINT.Boolean,
      clearable: PROP_TYPE_HINT.Boolean,
      loading: PROP_TYPE_HINT.Boolean,
    },
    exposedMethods: ["focus", "blur"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Datepicker]: {
    emits: ["focus", "blur", "close"],
    exposedMethods: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/datepicker/components/DSDatepicker.vue"),
    modelValueType: PROP_TYPE_HINT.Date,
    propTypes: {
      inlineEdit: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      clearable: PROP_TYPE_HINT.Boolean,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.Timepicker]: {
    emits: ["focus", "blur", "close"],
    exposedMethods: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/timepicker/components/DSTimePicker.vue"),
    modelValueType: PROP_TYPE_HINT.Time,
    propTypes: {
      inlineEdit: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      clearable: PROP_TYPE_HINT.Boolean,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.Fieldset]: {
    loadComponent: () => import("@/src/design_system/form_elements/fieldset/components/DSFieldset.vue"),
    propTypes: FIELDSET_PROP_TYPES,
    slots: ["actions"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Form]: {
    loadComponent: () => import("@/src/design_system/form_elements/form/components/DSForm.vue"),
    propTypes: { turbo: PROP_TYPE_HINT.Boolean },
    slots: ["actions"],
    exposedMethods: ["resetValidation", "setEasyErrors", "validate"],
  },
  [DS_CUSTOM_ELEMENT_NAME.FormLayout]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/form_layout/components/DSFormLayout.vue"),
    slots: ["footer"],
  },
  [DS_CUSTOM_ELEMENT_NAME.ActionButtons]: {
    loadComponent: () => import("@/src/design_system/form_elements/action_buttons/components/DSActionButtons.vue"),
    propTypes: { disabled: PROP_TYPE_HINT.Boolean },
  },
  [DS_CUSTOM_ELEMENT_NAME.IconPicker]: {
    emits: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/icon_picker/components/DSIconPicker.vue"),
    modelValueType: PROP_TYPE_HINT.String,
    propTypes: {
      inlineEdit: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      searchable: PROP_TYPE_HINT.Boolean,
      clearable: PROP_TYPE_HINT.Boolean,
    },
    exposedMethods: ["focus", "blur"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Dialog]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/dialog/components/DSDialog.vue"),
    propTypes: DIALOG_PROP_TYPES,
    slots: ["header", "subheader", "footer"],
    exposedMethods: ["close"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Accordion]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/accordion/components/DSAccordion.vue"),
    propTypes: {
      collapsible: PROP_TYPE_HINT.Boolean,
      expanded: PROP_TYPE_HINT.Boolean,
      defaultExpanded: PROP_TYPE_HINT.Boolean,
      withDivider: PROP_TYPE_HINT.Boolean,
    },
    slots: ["suffix"],
    emits: ["update:expanded"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Card]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/card/components/DSCard.vue"),
    propTypes: {
      collapsible: PROP_TYPE_HINT.Boolean,
      expanded: PROP_TYPE_HINT.Boolean,
    },
    slots: ["suffix", "actions"],
    emits: ["update:expanded"],
  },
  [DS_CUSTOM_ELEMENT_NAME.Divider]: {
    loadComponent: () => import("@/src/design_system/layouts_and_containers/divider/components/DSDivider.vue"),
  },
  [DS_CUSTOM_ELEMENT_NAME.CommentBubble]: {
    loadComponent: () => import("@/src/design_system/data_display/comment_bubble/components/DSCommentBubble.vue"),
    propTypes: { isOwn: PROP_TYPE_HINT.Boolean },
  },
} as const;
